import { type ReactNode, forwardRef } from 'react';
import clsx from 'clsx';
import { getImageProps } from '@hungryroot/image';
import { Heading } from '../typography/Heading/Heading';
import styles from './Tile.module.css';

type BaseProps = {
  testid?: string;
  heading: string;
  lead?: ReactNode;
  supportingCopy?: ReactNode;
  badge?: ReactNode;
  slideIn?: boolean;
  selected?: boolean;
  isFocusVisible?: boolean;
  isPressed?: boolean;
  disabled?: boolean; // Controls the visual treatment for disabled state. Caller should apply disabled attribute to interactive element as well.
  className?: string;
};

type ImageProps =
  | {
      imgSrc?: string;
      icon?: never;
      iconSize?: never;
    }
  | {
      imgSrc?: never;
      icon?: ReactNode;
      iconSize: 'small' | 'medium' | 'large';
    };

type AlignmentProps =
  | {
      alignment: 'left';
      likeDislike?: never;
    }
  | {
      alignment: 'center';
      likeDislike?: ReactNode;
    }
  | {
      alignment: 'center-left-aligned-content';
      likeDislike?: ReactNode;
    };

type TileProps = BaseProps & ImageProps & AlignmentProps;

export const Tile = forwardRef<HTMLDivElement, TileProps>(
  function Tile(props, ref) {
    const {
      testid,
      alignment,
      heading,
      lead,
      supportingCopy,
      badge,
      imgSrc,
      icon,
      iconSize,
      likeDislike,
      slideIn,
      selected,
      isFocusVisible,
      disabled,
      className,
    } = props;

    const isImageTitle = Boolean(imgSrc);
    const isHeadlineOnly = !supportingCopy && !imgSrc && !icon;

    return (
      <div
        ref={ref}
        data-testid={testid}
        className={clsx(
          styles.container,
          styles[`container-${alignment}`],
          className,
          {
            [styles['slide-in']]: slideIn,
            [styles['container-headline']]: isHeadlineOnly,
            [styles['container-image']]: isImageTitle,
            [styles.selected]: selected,
            [styles['focus-visible']]: isFocusVisible,
            [styles.disabled]: disabled,
          }
        )}
      >
        {(icon || imgSrc) && (
          <div
            className={clsx(styles['image-container'], {
              [styles['icon-small']]: iconSize === 'small',
              [styles['icon-medium']]: iconSize === 'medium',
              [styles['icon-large']]: iconSize === 'large',
            })}
          >
            {icon}
            {imgSrc && (
              <picture>
                {/* probably not realistic for a screen to go below this size but account for it anyway */}
                <source
                  media="only screen and (max-width: 314px)"
                  {...getImageProps({
                    src: imgSrc,
                    width: 200,
                  })}
                />
                <source
                  media="only screen and (min-width: 315px) and (max-width: 581px)"
                  {...getImageProps({
                    src: imgSrc,
                    width: 300,
                  })}
                />
                {/* on mobile, we only need 488 x 366, which matches the 600 width image */}
                <source
                  media="only screen and (min-width: 582px) and (max-width: 1024px)"
                  {...getImageProps({
                    src: imgSrc,
                    width: 600,
                  })}
                />
                {/* on desktop, we only need 181 x 136, which matches the 200 width image */}
                <source
                  media="only screen and (min-width: 1025px)"
                  {...getImageProps({
                    src: imgSrc,
                    width: 200,
                  })}
                />
                <img
                  data-testid={`${testid}-image`}
                  className={styles.image}
                  src={imgSrc}
                  alt=""
                />
              </picture>
            )}
          </div>
        )}
        <div className={styles.body}>
          <div className={styles['heading-wrapper']}>
            {lead}
            <Heading variant="H6">{heading}</Heading>
            {supportingCopy && (
              <div className={styles.supporting}>{supportingCopy}</div>
            )}
          </div>
          {badge}
        </div>
        {likeDislike && <div className={styles.action}>{likeDislike}</div>}
      </div>
    );
  }
);
